import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const PartnersSection = () => {
  const partners = [
  {
    id: 1,
    name: 'Vienna Business Agency',
    logo: "/assets/images/wirtschaftsagentur-1769704657252.png",
    url: 'https://viennabusinessagency.at',
    alt: 'Vienna Business Agency logo'
  },
  {
    id: 2,
    name: 'FFG',
    logo: '/assets/images/FFG_Logo_EN_RGB_1500px-1770285370863.png',
    url: 'https://www.ffg.at/',
    alt: 'FFG Austrian Research Promotion Agency logo'
  },
  {
    id: 4,
    name: 'A1 Digital',
    logo: "/assets/images/A1_Digital-1769698317761.png",
    url: 'https://www.a1.digital',
    alt: 'A1 Digital logo'
  },
  {
    id: 5,
    name: 'Austria Wirtschaftsservice (aws)',
    logo: "/assets/images/AWS_DeepTech-1769698694455.jpg",
    url: 'https://www.aws.at',
    alt: 'Austria Wirtschaftsservice (aws) logo'
  },
  {
    id: 6,
    name: 'BERG Propulsion',
    logo: "/assets/images/BERG-Logo-1769699047211.png",
    url: 'https://www.bergpropulsion.com',
    alt: 'BERG Propulsion logo'
  },
  {
    id: 7,
    name: 'CARIAD',
    logo: "/assets/images/cariad-1769699537200.jpg",
    url: 'https://www.cariad.technology',
    alt: 'CARIAD logo'
  },
  {
    id: 8,
    name: 'EIT Manufacturing',
    logo: "/assets/images/EITM_EU_CoFund-1769699649856.png",
    url: 'https://www.eitmanufacturing.eu',
    alt: 'EIT Manufacturing logo'
  },
  {
    id: 9,
    name: 'GLIWA',
    logo: "/assets/images/gliwa_logo_big_500x500-1769705422248.png",
    url: 'https://www.gliwa.com',
    alt: 'GLIWA logo'
  },
  {
    id: 10,
    name: 'HighTec EDV-Systeme',
    logo: "/assets/images/HighTec-logo-positive_RGB-1769700010691.png",
    url: 'https://www.hightec-rt.com',
    alt: 'HighTec EDV-Systeme logo'
  },
  {
    id: 11,
    name: 'Infineon Technologies',
    logo: "/assets/images/Infineon-1769700068326.png",
    url: 'https://www.infineon.com',
    alt: 'Infineon Technologies logo'
  },
  {
    id: 12,
    name: 'Lauterbach',
    logo: "/assets/images/Lauterbach_company_-Logo.wine-1769700171656.png",
    url: 'https://www.lauterbach.com',
    alt: 'Lauterbach logo'
  },
  {
    id: 13,
    name: 'Vector Informatik',
    logo: "/assets/images/Vector_Tech-1769700624764.png",
    url: 'https://www.vector.com',
    alt: 'Vector Informatik logo'
  },
  {
    id: 14,
    name: 'NVIDIA',
    logo: "/assets/images/nVidia-1769700786924.png",
    url: 'https://www.nvidia.com',
    alt: 'NVIDIA logo'
  },
  {
    id: 15,
    name: 'NXP Semiconductors',
    logo: "/assets/images/NXP-1769700986854.png",
    url: 'https://www.nxp.com',
    alt: 'NXP Semiconductors logo'
  },
  {
    id: 16,
    name: 'SOAFEE',
    logo: "/assets/images/soafee-1769702536586.png",
    url: 'https://www.soafee.io',
    alt: 'SOAFEE logo'
  },
  {
    id: 17,
    name: 'TTTech Auto',
    logo: "/assets/images/tttech_auto_logo-1769703771838.png",
    url: 'https://www.tttech-auto.com',
    alt: 'TTTech Auto logo'
  }];


  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const pauseTimeoutRef = useRef(null);

  // Auto-rotate every 2.4 seconds with smooth continuous rotation
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % partners?.length);
    }, 2400);

    return () => clearInterval(interval);
  }, [isPaused, partners?.length]);

  // Handle pause on hover
  const handleMouseEnter = () => {
    setIsHovered(true);
    setIsPaused(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setIsPaused(false);
  };

  // Handle manual navigation with brief pause then resume
  const handleManualNavigation = (direction) => {
    setIsPaused(true);
    
    if (direction === 'next') {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % partners?.length);
    } else {
      setCurrentIndex((prevIndex) => (prevIndex - 1 + partners?.length) % partners?.length);
    }

    // Clear existing timeout
    if (pauseTimeoutRef?.current) {
      clearTimeout(pauseTimeoutRef?.current);
    }

    // Resume auto-rotation after 3 seconds if not hovered
    pauseTimeoutRef.current = setTimeout(() => {
      if (!isHovered) {
        setIsPaused(false);
      }
    }, 3000);
  };

  // Handle pagination dot click
  const handleDotClick = (index) => {
    setIsPaused(true);
    setCurrentIndex(index);

    // Clear existing timeout
    if (pauseTimeoutRef?.current) {
      clearTimeout(pauseTimeoutRef?.current);
    }

    // Resume auto-rotation after 3 seconds if not hovered
    pauseTimeoutRef.current = setTimeout(() => {
      if (!isHovered) {
        setIsPaused(false);
      }
    }, 3000);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (pauseTimeoutRef?.current) {
        clearTimeout(pauseTimeoutRef?.current);
      }
    };
  }, []);

  // Get 3 visible items (with wrapping for infinite loop)
  const getVisiblePartners = () => {
    const visible = [];
    for (let i = 0; i < 3; i++) {
      const index = (currentIndex + i) % partners?.length;
      visible?.push(partners?.[index]);
    }
    return visible;
  };

  const visiblePartners = getVisiblePartners();

  return (
    <section className="py-24 px-4 md:px-6 lg:px-8 border-t border-border">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-2xl font-bold text-foreground mb-4 md:mb-6">
            Backed by Innovators and Research Leaders
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            We're proud to partner with leading organizations that share our vision for the future of hardware development.
          </p>
        </div>

        {/* Partner Logos Carousel */}
        <div
          className="relative"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}>

          {/* Navigation Arrows */}
          <button
            onClick={() => handleManualNavigation('prev')}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 z-10 bg-card border border-border rounded-full p-2 hover:bg-primary hover:border-primary hover:text-primary-foreground transition-all duration-300 shadow-lg"
            aria-label="Previous partners">
            <ChevronLeft className="w-5 h-5" />
          </button>

          <button
            onClick={() => handleManualNavigation('next')}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 z-10 bg-card border border-border rounded-full p-2 hover:bg-primary hover:border-primary hover:text-primary-foreground transition-all duration-300 shadow-lg"
            aria-label="Next partners">
            <ChevronRight className="w-5 h-5" />
          </button>

          <div className="overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
              <AnimatePresence mode="popLayout">
                {visiblePartners?.map((partner, idx) =>
                <motion.a
                  key={`${partner?.id}-${currentIndex}-${idx}`}
                  href={partner?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-card border border-border rounded-lg overflow-hidden hover:border-primary/50 hover:shadow-lg transition-all duration-300 group"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{
                    duration: 0.5,
                    ease: [0.4, 0, 0.2, 1]
                  }}>

                    {/* Logo Container - White Background */}
                    <div className="bg-white p-8 md:p-10 flex items-center justify-center min-h-[200px]">
                      <img
                      src={partner?.logo}
                      alt={partner?.alt}
                      className="max-w-full max-h-[120px] w-auto h-auto object-contain" />

                    </div>
                    
                    {/* Organization Name */}
                    <div className="bg-card p-4 text-center border-t border-border">
                      <h3 className="text-base font-semibold text-foreground group-hover:text-primary transition-colors duration-300">
                        {partner?.name}
                      </h3>
                    </div>
                  </motion.a>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Pagination Dots */}
        <div className="flex justify-center items-center gap-2 mt-8">
          {partners?.map((partner, index) => (
            <button
              key={partner?.id}
              onClick={() => handleDotClick(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentIndex
                  ? 'w-8 h-2 bg-primary' :'w-2 h-2 bg-border hover:bg-primary/50'
              }`}
              aria-label={`Go to ${partner?.name}`}
              aria-current={index === currentIndex ? 'true' : 'false'}
            />
          ))}
        </div>
      </div>
    </section>
  );

};

export default PartnersSection;